import { Button, Checkbox, CircularProgress, FormControlLabel, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FilterLine from '../../../components/filter/FilterLine';
import { exportToXlsx } from '../../../excelExportHelpers';
import { useSorting } from '../../../hooks/useSorting';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import BookingPaymentsTable from './components/BookingPaymentsTable';

const getDiscountPackageName = discountData => {
    const discount = discountData?.booking_item_ticket?.bookingItemDiscount?.discount_package;
    if (discount && discount.name) {
        return discount.name;
    }
    if (discountData && discountData.name) {
        return discountData.name;
    }
    return '';
};

const formatPaymentForExport = (payment, showDiscounts) => {
    const base = {
        guest: payment.guest,
        amount: payment.amount,
        ...(showDiscounts
            ? {}
            : {
                  type: payment.type,
                  last4: payment.last4,
                  note: payment.note
              }),
        bookedAt: moment.utc(payment.bookedAt).format('YYYY-MM-DD HH:mm:ss'),
        company: payment.company,
        activities: payment.activities.map(e => e.name).join('\n')
    };

    if (showDiscounts) {
        const agencyNames = [
            ...new Set(payment.agencyDiscount.map(e => getDiscountPackageName(e)).filter(Boolean))
        ];
        const vendorNames = [
            ...new Set(payment.vendorDiscount.map(e => getDiscountPackageName(e)).filter(Boolean))
        ];
        const agencyTotal = payment.agencyDiscount.reduce(
            (sum, e) => sum + Math.abs(e.net + e.tax),
            0
        );
        const vendorTotal = payment.vendorDiscount.reduce(
            (sum, e) => sum + Math.abs(e.net + e.tax),
            0
        );

        return {
            ...base,
            agencyDiscountName: agencyNames.length > 0 ? agencyNames.join('\n') : '-',
            agencyDiscountAmount: agencyTotal > 0 ? agencyTotal : '-',
            vendorDiscountName: vendorNames.length > 0 ? vendorNames.join('\n') : '-',
            vendorDiscountAmount: vendorTotal > 0 ? vendorTotal : '-'
        };
    }

    return {
        ...base,
        activityStart: payment.activities
            .map(e => moment.utc(e.start).format('YYYY-MM-DD HH:mm:ss'))
            .join('\n'),
        status: payment.activities
            .map(e => e.status)
            .filter(Boolean)
            .join(', '),
        paymentStatus: payment.refundStatus || ''
    };
};

const BOOKING_PAYMENTS_PAGE_FILTERS = [
    {
        key: 'activityStartDate',
        label: 'Activity start',
        rangeKeys: ['activityStartDate', 'activityEndDate'],
        type: 'daterange'
    },
    {
        key: 'bookedAtStartDate',
        label: 'Booked on',
        rangeKeys: ['bookedAtStartDate', 'bookedAtEndDate'],
        type: 'daterange'
    },
    {
        key: 'guestId',
        label: 'Guest name',
        type: 'guestautocomplete'
    },
    {
        key: 'clientId',
        label: 'Client',
        type: 'vendorautocomplete'
    }
];

function getFilterFromUrl(urlSearchPart) {
    const searchParams = new URLSearchParams(urlSearchPart.trim() !== '' ? urlSearchPart : '');

    const activityStartDate =
        searchParams.get('activityStartDate') !== null &&
        moment(searchParams.get('activityStartDate')).isValid()
            ? moment(searchParams.get('activityStartDate'))
            : null;
    const activityEndDate =
        searchParams.get('activityEndDate') !== null &&
        moment(searchParams.get('activityEndDate')).isValid()
            ? moment(searchParams.get('activityEndDate'))
            : null;
    const bookedAtStartDate =
        searchParams.get('bookedAtStartDate') !== null &&
        moment(searchParams.get('bookedAtStartDate')).isValid()
            ? moment(searchParams.get('bookedAtStartDate'))
            : null;
    const bookedAtEndDate =
        searchParams.get('bookedAtEndDate') !== null &&
        moment(searchParams.get('bookedAtEndDate')).isValid()
            ? moment(searchParams.get('bookedAtEndDate'))
            : null;

    const newClientId = parseInt(searchParams.get('clientId'), 10);
    const newGuestId = parseInt(searchParams.get('guestId'), 10);

    return {
        activityStartDate,
        activityEndDate,
        bookedAtStartDate,
        bookedAtEndDate,
        clientId: !Number.isNaN(newClientId) ? newClientId : '',
        guestId: !Number.isNaN(newGuestId) ? newGuestId : '',
        showDiscounts: searchParams.get('showDiscounts') || false
    };
}

function getUrlFromFilter(filter) {
    const search = new URLSearchParams();
    if (filter.activityStartDate) {
        search.append('activityStartDate', filter.activityStartDate.format('YYYY-MM-DD'));
    }
    if (filter.activityEndDate) {
        search.append('activityEndDate', filter.activityEndDate.format('YYYY-MM-DD'));
    }
    if (filter.bookedAtStartDate) {
        search.append('bookedAtStartDate', filter.bookedAtStartDate.format('YYYY-MM-DD'));
    }
    if (filter.bookedAtEndDate) {
        search.append('bookedAtEndDate', filter.bookedAtEndDate.format('YYYY-MM-DD'));
    }
    if (filter.clientId) {
        search.append('clientId', filter.clientId);
    }
    if (filter.guestId) {
        search.append('guestId', filter.guestId);
    }
    if (filter.showDiscounts) {
        search.append('showDiscounts', filter.showDiscounts);
    }
    const searchString = search.toString();
    return `?${searchString}`;
}

function BookingPaymentsPage() {
    const history = useHistory();
    const location = useLocation();
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));
    const { sort, sortDir, setSorting } = useSorting(['created_at', 'DESC']);
    const [generatingXlsx, setGeneratingXlsx] = useState(false);
    const [progress, setProgress] = useState(0);

    const setCurrentFilters = useCallback(
        newfilters => history.replace(getUrlFromFilter(newfilters)),
        [history]
    );

    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    const [data, loading, error] = useSwaggerClient('bookings', 'getBookingPayments', {
        page,
        pageSize,
        clientId: currentFilters.clientId,
        guestId: currentFilters.guestId,
        activityStartDate: moment(currentFilters.activityStartDate).isValid()
            ? moment(currentFilters.activityStartDate).format('YYYY-MM-DD')
            : '',
        activityEndDate: moment(currentFilters.activityEndDate).isValid()
            ? moment(currentFilters.activityEndDate).format('YYYY-MM-DD')
            : '',
        bookedAtStartDate: moment(currentFilters.bookedAtStartDate).isValid()
            ? moment(currentFilters.bookedAtStartDate).format('YYYY-MM-DD')
            : '',
        bookedAtEndDate: moment(currentFilters.bookedAtEndDate).isValid()
            ? moment(currentFilters.bookedAtEndDate).format('YYYY-MM-DD')
            : '',
        showDiscounts: currentFilters.showDiscounts,
        sort,
        sortDir
    });

    const [reports] = useSwaggerClient('reports', 'getCustomReports', {
        clientId: currentFilters.clientId
    });

    useEffect(() => {
        console.log('--- REPORTS ---', reports);
    }, [reports]);

    const resetFilters = () =>
        setCurrentFilters({
            activityStartDate: null,
            activityEndDate: null,
            bookedAtStartDate: null,
            bookedAtEndDate: null,
            clientId: '',
            guestId: '',
            showDiscounts: false
        });

    // TODO: can be refactored into excelExportHandler
    // The same pattern is present in payouts/index.js
    const collectAllData = async () => {
        const allPayout = [];
        const localPageSize = 10;
        const totalPayouts = data?.total;

        for (let i = 0; i < Math.ceil(totalPayouts / localPageSize); i++) {
            const res = await doSwaggerCall('bookings', 'getBookingPayments', {
                page: i,
                pageSize: localPageSize,
                clientId: currentFilters.clientId,
                guestId: currentFilters.guestId,
                activityStartDate: moment(currentFilters.activityStartDate).isValid()
                    ? moment(currentFilters.activityStartDate).format('YYYY-MM-DD')
                    : '',
                activityEndDate: moment(currentFilters.activityEndDate).isValid()
                    ? moment(currentFilters.activityEndDate).format('YYYY-MM-DD')
                    : '',
                bookedAtStartDate: moment(currentFilters.bookedAtStartDate).isValid()
                    ? moment(currentFilters.bookedAtStartDate).format('YYYY-MM-DD')
                    : '',
                bookedAtEndDate: moment(currentFilters.bookedAtEndDate).isValid()
                    ? moment(currentFilters.bookedAtEndDate).format('YYYY-MM-DD')
                    : '',
                showDiscounts: currentFilters.showDiscounts,
                sort,
                sortDir
            });
            res.bookingPayments.forEach(e => allPayout.push(e));
            setProgress(prevProgress => prevProgress + (localPageSize / totalPayouts) * 100);
        }

        return allPayout;
    };

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.bookingPayments?.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    return (
        <Grid container className="golf-reservation">
            <Grid container item xs={12}>
                <Grid item xs={12} container justifyContent="space-between">
                    <Grid item xs={10}>
                        <FilterLine
                            possibleFilters={BOOKING_PAYMENTS_PAGE_FILTERS}
                            filters={currentFilters}
                            setFilters={setCurrentFilters}
                            resetFilters={resetFilters}
                        />
                    </Grid>
                    <Grid
                        item
                        container
                        xs={2}
                        justifyContent="flex-end"
                        className="searchfilter-wrapper"
                    >
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={currentFilters.showDiscounts}
                                        onChange={() =>
                                            setFilters({
                                                ...currentFilters,
                                                showDiscounts: !currentFilters.showDiscounts
                                            })
                                        }
                                    />
                                }
                                label="Show discounts"
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={async () => {
                                    setGeneratingXlsx(true);

                                    let listToExport = [];
                                    try {
                                        const rawData = await collectAllData();
                                        listToExport = rawData.map(payment =>
                                            formatPaymentForExport(
                                                payment,
                                                currentFilters.showDiscounts
                                            )
                                        );
                                    } catch (err) {
                                        console.log(err);
                                    }
                                    exportToXlsx(listToExport, 'payments');
                                    setGeneratingXlsx(false);
                                    setProgress(0);
                                }}
                            >
                                {generatingXlsx ? (
                                    <CircularProgress
                                        variant="determinate"
                                        color="secondary"
                                        size={25}
                                        value={progress}
                                    />
                                ) : (
                                    'Export to XLSX'
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                {error ? (
                    <Grid item xs={12}>
                        <Alert severity="error">
                            <h4>{error.response.body.message || error.message}</h4>
                        </Alert>
                    </Grid>
                ) : loading && data?.bookingPayments?.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <BookingPaymentsTable
                        data={data}
                        sort={sort}
                        sortDir={sortDir}
                        setSorting={setSorting}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        page={page}
                        setPage={setPage}
                        showDiscounts={currentFilters.showDiscounts}
                    />
                )}
            </Grid>
        </Grid>
    );
}

export default BookingPaymentsPage;
